import { Controller, FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useEntityUpdateMutation } from 'app/agriculture/mutations/entity/update/useEntityUpdateMutation';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { Dialog } from 'app/agriculture/components/Dialog/Dialog';
import { IconSeparatedColumns } from 'app/agriculture/components/IconSeparatedColumns/IconSeparatedColumns';
import { LinkIcon } from 'app/agriculture/icons';
import Autocomplete from '@mui/material/Autocomplete';
import { TankLinkAutocompleteOption } from 'app/agriculture/routes/Tanks/TankLink/TankLinkAutocompleteOption';
import { Tank } from 'app/agriculture/queries/tanks/types/Tank';
import { FormHelperText, TextField } from '@mui/material';
import { ErrorWidget } from 'app/agriculture/error_widget';
import { Button } from 'app/agriculture/components/Button/Button';
import { Device } from 'app/agriculture/queries/devices/types/Device';
import { useContext } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { useNavigate } from 'react-router-dom';
import { useEntities } from 'app/agriculture/queries/entities/useEntities';
import { SensorCalibration } from 'app/agriculture/routes/Tanks/TankLink/components/SensorCalibration';
import { ContentLayout } from 'app/agriculture/components/ContentLayout/ContentLayout';
import { useDevicesByType } from 'app/agriculture/queries/devices/useDevicesByType';

interface FormValues {
    tank: Tank | null;
    device: Device | null;
    tankMaxVolume: number | null;
    tankMaxHeight: number | null;
    tankCalibrationOffset: number | null;
}

interface TankLinkFormProps {
    defaultTank: Tank | null;
    defaultDevice: Device | null;
    entityRootPath: 'tanks' | 'devices';
}

export function TankLinkForm({ defaultTank, defaultDevice, entityRootPath }: TankLinkFormProps) {
    const { agricultureToastService } = useContext(ServicesContext);
    const navigate = useNavigate();
    const tanks = useEntities('Tank');
    const devices = useDevicesByType('Level Monitor');

    const form = useForm<FormValues>({
        defaultValues: {
            tank: defaultTank,
            device: defaultDevice,
            tankMaxHeight: defaultDevice?.tankMaxHeight,
            tankCalibrationOffset: defaultDevice?.tankCalibrationOffset,
            // convert volume value from m3 to L
            tankMaxVolume:
                defaultDevice?.tankMaxVolume == null ? null : defaultDevice.tankMaxVolume * 1000,
        },
        mode: 'all',
        shouldFocusError: true,
    });
    form.watch('device');
    form.watch('tank');
    const device = useWatch({ control: form.control, name: 'device' });

    const deviceType = form.getValues().device?.brand === 'Yarn' ? 'Device' : 'GasbotDevice';
    const deviceMutation = useEntityUpdateMutation(deviceType);

    const onSubmit: SubmitHandler<FormValues> = ({
        tank,
        device,
        tankMaxVolume,
        tankMaxHeight,
        tankCalibrationOffset,
    }) => {
        if (tank == null || device == null || tankMaxVolume == null || tankMaxHeight == null) {
            console.error('invalid form');
            void agricultureToastService.showToast({
                title: 'Error linking device',
                type: AgricultureToastType.ERROR,
                variant: 'simple',
            });

            return;
        }

        deviceMutation.mutate(
            {
                id: device.id,
                infrastructureId: tank.id,
                tankCalibrationOffset,
                tankMaxHeight,
                tankMaxVolume: tankMaxVolume / 1000,
            },
            {
                onSuccess: () => {
                    void agricultureToastService.showToast({
                        title: 'Device Linked',
                        type: AgricultureToastType.SUCCESS,
                        variant: 'simple',
                    });
                    navigate('..', { relative: 'path' });
                },
            },
        );
    };

    return (
        <Dialog
            title={entityRootPath === 'tanks' ? 'Link Tank' : 'Link Device'}
            subtitle={
                entityRootPath === 'tanks'
                    ? 'Choose a device to link to the water tank'
                    : 'Choose a water tank to link to the device'
            }
            className="TankLink"
            handleClose={() => {
                navigate('..', { relative: 'path' });
            }}
        >
            <FormProvider {...form}>
                <ContentLayout gap={64}>
                    <ContentLayout gap={36}>
                        <IconSeparatedColumns Icon={LinkIcon} flip={entityRootPath === 'devices'}>
                            <Controller
                                name="tank"
                                control={form.control}
                                rules={{
                                    required: 'Please select a tank',
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <Autocomplete
                                            fullWidth
                                            disabled={entityRootPath === 'tanks'}
                                            options={[...tanks.values()]}
                                            value={field.value}
                                            isOptionEqualToValue={(option, value) =>
                                                option.id === value.id
                                            }
                                            renderOption={(
                                                optionProps,
                                                option,
                                                _,
                                                { getOptionLabel, getOptionDisabled = () => false },
                                            ) => (
                                                <TankLinkAutocompleteOption
                                                    key={option.id}
                                                    name={getOptionLabel(option)}
                                                    isDisabled={!getOptionDisabled(option)}
                                                    optionProps={optionProps}
                                                />
                                            )}
                                            getOptionLabel={(tank) => tank.name}
                                            getOptionDisabled={(tank: Tank) =>
                                                [...devices.values()].some(
                                                    (device) => device.infrastructureId === tank.id,
                                                )
                                            }
                                            autoComplete
                                            onChange={(_event, tank) => {
                                                field.onChange(tank);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Water Tank"
                                                    placeholder="Select Tank..."
                                                />
                                            )}
                                        />
                                        {fieldState.error == null ? null : (
                                            <FormHelperText error>
                                                <ErrorWidget
                                                    errorMessage={fieldState.error.message}
                                                />
                                            </FormHelperText>
                                        )}
                                    </>
                                )}
                            />
                            <Controller
                                name="device"
                                control={form.control}
                                rules={{
                                    required: 'Please select a device',
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <Autocomplete
                                            fullWidth
                                            disabled={entityRootPath === 'devices'}
                                            options={[...devices.values()]}
                                            value={field.value}
                                            isOptionEqualToValue={(option, value) =>
                                                option.id === value.id
                                            }
                                            renderOption={(
                                                optionProps,
                                                option,
                                                _,
                                                { getOptionLabel, getOptionDisabled = () => false },
                                            ) => (
                                                <TankLinkAutocompleteOption
                                                    key={option.id}
                                                    name={getOptionLabel(option)}
                                                    isDisabled={!getOptionDisabled(option)}
                                                    optionProps={optionProps}
                                                />
                                            )}
                                            getOptionLabel={({ name }) => name}
                                            getOptionDisabled={({ infrastructureId }) =>
                                                infrastructureId != null
                                            }
                                            autoComplete
                                            onChange={(_event, device) => {
                                                field.onChange(device);
                                                form.setValue(
                                                    'tankMaxVolume',
                                                    device?.tankMaxVolume == null
                                                        ? null
                                                        : device.tankMaxVolume * 1000,
                                                );
                                                form.setValue(
                                                    'tankMaxHeight',
                                                    device?.tankMaxHeight ?? null,
                                                );
                                                form.setValue(
                                                    'tankCalibrationOffset',
                                                    device?.tankCalibrationOffset ?? null,
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Device"
                                                    placeholder="Select Device..."
                                                />
                                            )}
                                        />
                                        {fieldState.error == null ? null : (
                                            <FormHelperText error>
                                                <ErrorWidget
                                                    errorMessage={fieldState.error.message}
                                                />
                                            </FormHelperText>
                                        )}
                                    </>
                                )}
                            />
                        </IconSeparatedColumns>
                        {device == null || form.getValues().tank == null ? null : (
                            <SensorCalibration brand={device.brand} />
                        )}
                    </ContentLayout>
                    <Button
                        id="tank-link-submit"
                        text="Confirm Link"
                        fullWidth
                        disabled={
                            form.getValues().device?.infrastructureId === form.getValues().tank?.id
                        }
                        onClick={() => {
                            void form.handleSubmit(onSubmit)();
                        }}
                    />
                </ContentLayout>
            </FormProvider>
        </Dialog>
    );
}
